import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Button } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import { ArrowLeftIcon } from "lucide-react";
import { useEffect } from "react";
import { useMyDocumentDetailQuery, useUpdateMyDocument } from "../../api/queries";
import { myDocumentsApi } from "../../api/my-documents-client";
import { DocumentCreateForm } from "../../ui/DocumentCreateForm";

export function MyDocumentEditPage() {
  const { id } = useParams({ from: "/app/my-documents/$id/edit" });
  const nav = useNavigate({ from: "/app/my-documents/$id/edit" });
  const { data, isLoading } = useMyDocumentDetailQuery(id ?? "");
  const updateMutation = useUpdateMyDocument();

  useEffect(() => {
    if (!isLoading && data?.document.status !== "draft" && id) {
      nav({ to: "/app/my-documents/$id", params: { id } });
    }
  }, [data?.document.status, id, isLoading, nav]);

  const isRedirecting = !isLoading && data && data.document.status !== "draft";
  if (isRedirecting) return null;

  const handleBack = () =>
    nav({ to: "/app/my-documents/$id", params: { id: id ?? "" } });

  const handleSubmit = (payload: {
    documentType: string;
    status: "draft" | "issued";
    documentNumber: string;
    customerId: string | null;
    customerName: string;
    customerAddress: string | null;
    customerContact: string | null;
    issueDate: string | null;
    dueDate: string | null;
    referencePo: string | null;
    paymentTerms: string | null;
    notes: string | null;
    templateId: string | null;
    items: Array<{
      description: string;
      detail?: string | null;
      quantity: number;
      unit: string | null;
      unitPrice: number;
      discountAmount?: number;
      taxRate?: number;
    }>;
    discountAmount: number;
    taxRate: number;
    depositPercent: number;
    currency: string;
    showSignature: boolean;
  }) => {
    if (!id) return;
    updateMutation.mutate(
      {
        id,
        input: {
          documentType: payload.documentType,
          status: payload.status,
          documentNumber: payload.documentNumber,
          customerId: payload.customerId ?? null,
          customerName: payload.customerName,
          customerAddress: payload.customerAddress ?? null,
          customerContact: payload.customerContact ?? null,
          issueDate: payload.issueDate ?? null,
          dueDate: payload.dueDate ?? null,
          currency: payload.currency,
          paymentTerms: payload.paymentTerms ?? null,
          notes: payload.notes ?? null,
          templateId: payload.templateId ?? null,
          items: payload.items.map((row) => ({
            description: row.description,
            detail: row.detail ?? null,
            quantity: row.quantity,
            unit: row.unit ?? null,
            unitPrice: row.unitPrice,
            discountAmount: row.discountAmount,
            taxRate: row.taxRate,
          })),
          discountAmount: payload.discountAmount,
          taxRate: payload.taxRate,
          depositPercent: payload.depositPercent,
          showSignature: payload.showSignature,
        },
      },
      {
        onSuccess: () => {
          nav({ to: "/app/my-documents/$id", params: { id } });
        },
      },
    );
  };

  return (
    <>
      <Header />
      <Main>
        <div className="space-y-2">
          <div className="mb-4 flex items-center justify-between space-x-2">
            <h1 className="font-bold text-2xl tracking-tight">ແກ້ໄຂເອກະສານ</h1>
            <Button
              type="button"
              variant="default"
              size="sm"
              onClick={handleBack}
              className="gap-2"
            >
              <ArrowLeftIcon className="h-4 w-4" />
              ກັບ
            </Button>
          </div>
        </div>

        {isLoading && <p className="text-muted-foreground">ກຳລັງໂຫຼດ...</p>}
        {data && data.document.status === "draft" && (
          <div data-tourid="document-edit-form">
            <DocumentCreateForm
              key={data.document.id}
              remoteApi={myDocumentsApi}
              initialData={{
                document: data.document,
                items: data.items,
              }}
              onSubmit={handleSubmit}
              submitting={updateMutation.isPending}
              portalMode
            />
          </div>
        )}
      </Main>
    </>
  );
}
